import {
  setTrackedDelay,
  setLiveStreamStatus,
  setActionEventIndex,
  setEventStarted,
  setEventEnded
} from "../../redux/actions";
import { SocketEvents } from "./SocketEvents";

let sockets = [];
const preventReconnect = new Set();
const connecting = new Set();

/**
 * Returns a stable UUID for this browser session (persisted in sessionStorage).
 * Used as subscriberId so the server can detect duplicate connections from the same tab.
 */
function getOrCreateSubscriberId() {
  const key = 'ws_subscriber_id';
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(key, id);
  }
  return id;
}

/**
 * Generates a signed WebSocket URL using HMAC
 */
export async function generateSignedUrl(url) {
  const encoder = new TextEncoder();
  const secretKeyData = encoder.encode("2qySfsbI5akSqHmj_empreinte_bNLt1pnS4I");
  const key = await crypto.subtle.importKey(
    "raw",
    secretKeyData,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );

  const expirationMs = 3 * 60 * 1000; // 3 minutes
  const expiry = Date.now() + expirationMs;
  const dataToAuthenticate = `${url.pathname}@${expiry}`;
  const mac = await crypto.subtle.sign("HMAC", key, encoder.encode(dataToAuthenticate));
  const base64Mac = btoa(String.fromCharCode(...new Uint8Array(mac)));

  url.searchParams.set("mac", base64Mac.replace(/\+/g, "-"));
  url.searchParams.set("expiry", expiry.toString());

  return url.toString();
}

/**
 * Helper to find socket by room
 */
export const getSocketByRoom = (roomId) =>
  sockets.find(s => s.id === roomId);

/**
 * Initialize or reuse a WebSocket for a live event
 */
export const initSocketLive = async (id, status, msg, store) => {
  // Check for an existing active socket
  let existingSocketEntry = sockets.find(s => s.id === id);

  // If socket exists and is open → reuse it
  if (existingSocketEntry && existingSocketEntry.socket?.readyState === WebSocket.OPEN) {
    if (status !== null) {
      existingSocketEntry.socket.send(
        JSON.stringify({ type: status ? "event_ended" : "event_started" })
      );
      // Don't create a new closure — the old one's status is now stale.
      // Nothing more to do: the send happened, no reconnect logic needed here.
    }

    if (msg) {
      setTimeout(() => {
        existingSocketEntry.socket.send(JSON.stringify({ type: msg }));
      }, 5000);
    }

    return; // Do not create a new connection
  }

  let retryCount = 0;

  /**
   * Internal function to connect or reconnect WebSocket
   */
  const connectWebSocket = async () => {
    // Clear stale prevent-reconnect flag so natural reconnects work after remount
    preventReconnect.delete(id);
    connecting.add(id);

    let wsUrl = window.process?.env?.WS_LIVE_URL || "";

    if (!wsUrl.includes("wss://")) {
      wsUrl = "wss:" + wsUrl;
    }

    const subscriberId = getOrCreateSubscriberId();
    wsUrl += `/?room=${id}&role=admin&subscriberId=${subscriberId}`;
    const signedWsUrl = await generateSignedUrl(new URL(wsUrl));

    const socket = new WebSocket(signedWsUrl);
    let rejoined = false;
    let pingInterval = null;

    const rejoin = async () => {
      if (preventReconnect.has(id)) return;
      if (!rejoined) {
        rejoined = true;
        // Exponential backoff: 1s, 2s, 4s, 8s — capped at 30s
        const delay = Math.min(1000 * Math.pow(2, retryCount), 30000);
        retryCount++;
        await new Promise(res => setTimeout(res, delay));
        connectWebSocket();
      }
    };

    socket.onopen = () => {
      connecting.delete(id);
      retryCount = 0; // reset backoff on successful connection

      // Replace stale entry or add new — fixes event_crud not sending after reconnect
      const existingIndex = sockets.findIndex(s => s.id === id);
      if (existingIndex !== -1) {
        sockets[existingIndex] = { id, socket };
      } else {
        sockets.push({ id, socket });
      }

      pingInterval = setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: 'ping', data: { id: crypto.randomUUID(), lastPingMs: Date.now() } }));
        }
      }, 30000);

      // Send event status if provided — nulled out after first send so reconnects don't re-send it
      if (status !== null) {
        socket.send(JSON.stringify({ type: status ? "event_ended" : "event_started" }));
        status = null;
      }

      // Send optional message after delay
      if (msg) {
        setTimeout(() => {
          socket.send(JSON.stringify({ type: msg }));
        }, 5000);
      }
    };

    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      switch (data.type) {
        case SocketEvents.trackEventDelay:
          store.dispatch(setTrackedDelay(data.data?.delay || 0));
          break;

        case SocketEvents.liveStream:
          store.dispatch(setLiveStreamStatus(data.data?.status || 0));
          break;

        case SocketEvents.EventCrud:
          if (data.updateType === "partial" && data.updatedIndex) {
            store.dispatch(setActionEventIndex({
              updateType: "partial",
              updatedIndex: {
                ...data.updatedIndex,
                eventId: data.eventId || id
              },
              eventId: data.eventId || id
            }));
          } else if (data.updateType === "delete" && data.chapterId) {
            store.dispatch(setActionEventIndex({
              updateType: "delete",
              deletedId: data.deletedId,
              chapterId: data.chapterId,
              eventId: data.eventId || id
            }));
          } else if (data.indexes) {
            store.dispatch(setActionEventIndex({
              indexes: data.indexes,
              eventId: data.eventId || id
            }));
          }
          break;

        case SocketEvents.EventStarted:
          store.dispatch(setEventStarted(id));
          break;

        case SocketEvents.EventEnded:
          store.dispatch(setEventEnded(id));
          break;

        case 'healthcheck':
        case 'pong':
          // server keep-alive messages — no action needed
          break;

        default:
          console.warn(`⚠️ Unknown socket event type: ${data.type}`);
          break;
      }
    };

    socket.onerror = () => {
      connecting.delete(id);
      clearInterval(pingInterval);
      socket.close();
      rejoin();
    };

    socket.onclose = () => {
      connecting.delete(id);
      clearInterval(pingInterval);
      // Remove stale entry so next reconnect adds the fresh socket
      sockets = sockets.filter(s => s.id !== id);
      rejoin();
    };
  };

  // Only create connection if none exists, not already connecting, or it's closed
  if (!connecting.has(id) && (!existingSocketEntry || existingSocketEntry.socket.readyState !== WebSocket.OPEN)) {
    await connectWebSocket();
  }

  // Cleanup handler — prevent reconnection on intentional close (unmount)
  return () => {
    const socketToClose = sockets.find(s => s.id === id);
    if (socketToClose) {
      preventReconnect.add(id);
      socketToClose.socket.close();
      sockets = sockets.filter(s => s.id !== id);
    }
  };
};

/**
 * Close all sockets except the one specified
 */
export const closeAllSocketsExcept = (keepId) => {
  sockets.forEach(({ socket, id }) => {
    if (id === keepId) return;

    preventReconnect.add(id);

    socket.onopen = null;
    socket.onmessage = null;
    socket.onerror = null;
    socket.onclose = null;

    socket.close();
  });

  sockets = sockets.filter(s => s.id === keepId);
};

/**
 * Send a message through an existing socket
 */
export const sendSocketEvent = (id, eventType, payload = {}) => {
  const socketEntry = sockets.find(s => s.id === id);

  if (socketEntry && socketEntry.socket?.readyState === WebSocket.OPEN) {
    const message = {
      type: eventType,
      ...payload,
      eventId: payload.eventId || id,
      timestamp: Date.now()
    };
    socketEntry.socket.send(JSON.stringify(message));
  } else {
    console.warn(`⚠️ Socket not found or not open for id: ${id}`);
  }
};

/**
 * Get all active sockets (useful for debugging)
 */
export const getActiveSockets = () => {
  return sockets.map(s => ({
    id: s.id,
    state: s.socket.readyState,
    stateText: ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][s.socket.readyState]
  }));
};

/**
 * Force close a specific socket
 */
export const closeSocket = (id) => {
  const socketEntry = sockets.find(s => s.id === id);

  if (socketEntry) {
    preventReconnect.add(id);
    socketEntry.socket.close();
    sockets = sockets.filter(s => s.id !== id);
    return true;
  }

  return false;
};

/**
 * Clear prevent reconnect flag (useful when navigating away)
 */
export const clearPreventReconnect = (id) => {
  preventReconnect.delete(id);
};
